import React from 'react';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'Douleutaras/redux/hooks';
import { sellerProfilesSelectors } from 'Douleutaras/redux/data/seller/sellerProfilesSlice';
import { sellerProfilesCategoriesSelectors } from 'Douleutaras/redux/data/seller/sellerProfilesCategoriesSlice';
import { sellerProfilesMunicipalitiesSelectors } from 'Douleutaras/redux/data/seller/sellerProfilesMunicipalitiesSlice';
import { sellerReviewsSelectors, singleSellerReviewsEndpoint } from 'Douleutaras/redux/data/seller/sellerReviewsSlice';
import { usersSelector } from 'Douleutaras/redux/data/users/usersSlice';
import { buildUrl, VALID_URLS } from 'Douleutaras/utilities/url-builder';

import { getSchemaTypeForCategory } from './settings/schemaTypeMap';

interface Props {
  username: string;
}

export const ProProfileSchema = ({ username }: Props) => {
  const { t } = useTranslation();
  const profile = useAppSelector(state => sellerProfilesSelectors.selectByUsername(state, username));

  const autodealCategoryIds = profile?.relationships.autodealCategories.data.map(c => c.id) ?? [];
  const autodealCategories = useAppSelector(state =>
    autodealCategoryIds.map(id => sellerProfilesCategoriesSelectors.selectById(state, id))
      .filter(Boolean)
  );

  const municipalityIds = profile?.relationships.municipalities?.data.map(m => m.id) ?? [];
  const municipalities = useAppSelector(state =>
    municipalityIds.map(id => sellerProfilesMunicipalitiesSelectors.selectById(state, id))
      .filter(Boolean)
  );

  // Collect up to 3 reviews (pinned + page-1) that have a comment, for ld+json
  const reviewsForSchema = useAppSelector(state => {
    const endpoint = singleSellerReviewsEndpoint(username);
    const pageMeta = (state.sellerReviews.meta as Record<string, { data: Array<{ id: string }> } | undefined>)[endpoint];
    const pinnedId = state.sellerReviews.pinnedItemId;

    const candidateIds: Array<string> = [
      ...(pinnedId ? [pinnedId] : []),
      ...(pageMeta?.data?.map(d => d.id) ?? [])
    ];

    const seen = new Set<string>();
    const result: Array<{
      '@type': string;
      author: { '@type': string; name: string };
      reviewRating: { '@type': string; ratingValue: number; bestRating: number; worstRating: number };
      reviewBody: string;
      datePublished: string;
    }> = [];

    for (const id of candidateIds) {
      if (seen.has(id) || result.length >= 3) { break; }
      seen.add(id);

      const entity = sellerReviewsSelectors.selectById(state, id);
      if (!entity?.attributes?.comment) { continue; }

      const rels = (entity as { relationships: Record<string, { data: { id: string } | undefined } | undefined> }).relationships;
      const buyerId: string | undefined = rels?.['client']?.data?.id ?? rels?.['buyer']?.data?.id;
      const reviewer = buyerId ? usersSelector.selectById(state, buyerId) : undefined;

      let authorName = 'Anonymous';
      if (reviewer?.attributes) {
        const attrs = reviewer.attributes as Record<string, string>;
        if (reviewer.type === 'externalClient') {
          authorName = `${attrs['firstName']} ${attrs['lastName'][0]}.`;
        } else if (attrs['sellerDisplayName']) {
          const parts = attrs['sellerDisplayName'].split(' ');
          const initial = parts[1]?.[0]?.toUpperCase() ?? '';
          authorName = parts.length >= 2
            ? `${parts[0]} ${initial}.`
            : attrs['sellerDisplayName'];
        }
      }

      result.push({
        '@type': 'Review',
        author: { '@type': 'Person', name: authorName },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: Number(entity.attributes.rating),
          bestRating: 5,
          worstRating: 1
        },
        reviewBody: entity.attributes.comment,
        datePublished: (entity.attributes.created ?? '').slice(0, 10)
      });
    }

    return result;
  });

  if (!profile) { return <></>; }

  const primaryCategory = autodealCategories.find(
    c => c.attributes.title === profile.attributes.favoriteCategory
  ) ?? autodealCategories[0];
  const primaryCategorySlug = typeof primaryCategory?.attributes.slug === 'string'
    ? primaryCategory.attributes.slug
    : undefined;

  const profileUrl = `${window.location.origin}${buildUrl(VALID_URLS.PROFILE, profile.attributes.username)}/`;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': getSchemaTypeForCategory(primaryCategorySlug),
    name: profile.attributes.sellerDisplayName,
    image: profile.attributes.mugshot,
    '@id': `${profileUrl}#business`,
    url: profileUrl,
    ...(profile.attributes.sellerFeedbacks > 0 ? {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: Number(profile.attributes.sellerRating),
        reviewCount: profile.attributes.sellerFeedbacks,
        bestRating: 5,
        worstRating: 1
      }
    } : {}),
    ...(municipalities.length > 0 ? {
      areaServed: municipalities.map(m => ({
        '@type': 'Place',
        name: m.attributes.title
      }))
    } : {}),
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: profile.attributes.favoriteCategory || 'Offer',
      itemListElement: autodealCategories.map(category => ({
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: category.attributes.title,
          serviceType: category.attributes.title
        }
      }))
    },
    ...(reviewsForSchema.length > 0 ? { review: reviewsForSchema } : {})
  };

  const breadcrumbData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: t('T.BREADCRUMB.HOME'),
        item: `${window.location.origin}/`
      },
      ...(primaryCategorySlug ? [{
        '@type': 'ListItem',
        position: 2,
        name: primaryCategory.attributes.title,
        item: `${window.location.origin}${buildUrl(VALID_URLS.BSDIR_CATEGORY, primaryCategorySlug)}`
      }] : []),
      {
        '@type': 'ListItem',
        position: primaryCategorySlug ? 3 : 2,
        name: profile.attributes.sellerDisplayName,
        item: profileUrl
      }
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbData)}
      </script>
    </Helmet>
  );
};
